import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  type MessageMethod,
  MessageMethodEnum,
  ShopFilter,
  type WatchAlertView,
} from "@b2bportal/air-price-watch-api";
import { useI18nContext, Trans } from "@hopper-b2b/i18n";
import { MobileFloatingButton, MobilePopoverCard } from "@hopper-b2b/ui";
import { SessionContext } from "@hopper-b2b/utilities";
import clsx from "clsx";
import CloseIcon from "../../assets/client/close.svg?react";
import MobileInfoPopover from "../MobileInfoPopover";
import { ToggleRowItem } from "../ToggleRowItem";
import styles from "./styles.module.scss";

const PriceWatchPopoverContent = ({
  onSubmit,
  showWarningOnClose,
  shouldShowWarningOnClose,
  watch,
}: {
  onSubmit: (method: { method: MessageMethod }, filter: ShopFilter) => void;
  showWarningOnClose: boolean;
  shouldShowWarningOnClose: Dispatch<SetStateAction<boolean>>;
  watch: WatchAlertView;
}) => {
  const [nonstopOnlySelected, setNonstopOnlySelected] = useState(false);
  const [excludeBasicSelected, setExcludeBasicSelected] = useState(false);
  const [emailSelected, setEmailSelected] = useState(
    watch ? watch.method?.email : true
  );
  const { email: sessionEmail } = useContext(SessionContext);
  const [notificationsSelected, setNotificationsSelected] = useState(
    watch ? watch.method?.push : true
  );
  const [openLearnMore, setOpenLearnMore] = useState(false);

  const { t } = useI18nContext();

  const handleClickLearnMore = useCallback(
    (e) => {
      e.stopPropagation();
      setOpenLearnMore(true);
    },
    [setOpenLearnMore]
  );

  const handleCloseLearnMore = useCallback(
    () => setOpenLearnMore(false),
    [setOpenLearnMore]
  );

  const notificationsSelectedHandler = useCallback(
    (isSelected: boolean) => {
      setNotificationsSelected(isSelected);
      shouldShowWarningOnClose(
        (!isSelected && emailSelected) || (isSelected && !emailSelected)
      );
    },
    [emailSelected, shouldShowWarningOnClose]
  );

  const emailSelectedHandler = useCallback(
    (isSelected: boolean) => {
      setEmailSelected(isSelected);
      shouldShowWarningOnClose(
        (!isSelected && notificationsSelected) ||
          (isSelected && !notificationsSelected)
      );
    },
    [notificationsSelected, shouldShowWarningOnClose]
  );

  const content = useMemo(
    () => ({
      flightTypes: [
        {
          title: t("priceWatch.nonstopOnly"),
          selected: nonstopOnlySelected,
          onCheckClick: setNonstopOnlySelected,
        },
        {
          title: t("priceWatch.excludeBasic"),
          selected: excludeBasicSelected,
          onCheckClick: setExcludeBasicSelected,
        },
      ],
      notificationMethods: [
        {
          title: t("priceWatch.appNotifications"),
          subtitle: (
            <Trans
              i18nKey="priceWatch.appNotificationsSubtitle"
              components={[
                <p></p>,
                <span></span>,
                <button
                  className="learn-more-link"
                  onClick={(e) => handleClickLearnMore(e)}
                ></button>,
              ]}
            />
          ),
          selected: notificationsSelected,
          onClick: notificationsSelectedHandler,
        },
        {
          title: t("priceWatch.emailDefault"),
          subtitle: sessionEmail,
          selected: emailSelected,
          onClick: emailSelectedHandler,
        },
      ],
    }),
    [
      t,
      nonstopOnlySelected,
      excludeBasicSelected,
      notificationsSelected,
      notificationsSelectedHandler,
      sessionEmail,
      emailSelected,
      emailSelectedHandler,
      handleClickLearnMore,
    ]
  );

  const onSubmitHandler = useCallback(() => {
    const priceWatchInfo: { method: MessageMethod } = {
      method: {
        MessageMethod: MessageMethodEnum.MultiMethod,
        ...(emailSelected && { email: { email: sessionEmail } }),
        ...(notificationsSelected && { push: {} }),
      },
    };

    const filter =
      nonstopOnlySelected && excludeBasicSelected
        ? ShopFilter.NonStopNoLCC
        : nonstopOnlySelected
          ? ShopFilter.NonStop
          : excludeBasicSelected
            ? ShopFilter.NoLCC
            : ShopFilter.NoFilter;

    onSubmit(priceWatchInfo, filter);
  }, [
    sessionEmail,
    emailSelected,
    excludeBasicSelected,
    nonstopOnlySelected,
    notificationsSelected,
    onSubmit,
  ]);

  return (
    <>
      <div className={styles.PriceWatchPopoverContent}>
        {/* <div className="preferences flight">
          <h4>{t("priceWatch.preferences")}</h4>
          {content.flightTypes.map((typeOption, i) => (
            <CheckboxRowItem {...typeOption} key={i} />
          ))}
        </div> */}
        <div className="preferences notify">
          <h4>{t("priceWatch.alertPreferences")}</h4>
          {content.notificationMethods.map((notificationMethod, i) => (
            <ToggleRowItem {...notificationMethod} key={i} />
          ))}
        </div>
      </div>
      <MobileFloatingButton
        onClick={onSubmitHandler}
        wide
        // default state is both selected, disable if both are selected or both are not selected
        disabled={
          (!emailSelected && !notificationsSelected) ||
          (emailSelected && notificationsSelected)
        }
        topContent={
          !emailSelected && !notificationsSelected ? (
            <p className="helper-text">
              {t("priceWatch.oneNotificationActive")}
            </p>
          ) : null
        }
        className={styles.PriceWatchFloatingButton}
      >
        {t("priceWatch.update")}
      </MobileFloatingButton>
      <MobilePopoverCard
        open={openLearnMore}
        onClose={handleCloseLearnMore}
        className={clsx(styles.PriceWatchLearnMoreModal, "half-sheet")}
        contentClassName="flight-watch-opt-in-content-wrapper"
        topLeftButton={
          <button className="half-sheet-close" onClick={handleCloseLearnMore}>
            <CloseIcon className={styles.Close} />
          </button>
        }
      >
        <>
          <h2>{t("priceWatch.learnMore.title")}</h2>
          <Trans i18nKey="priceWatch.learnMore.content" components={[<p />]} />
          <MobileFloatingButton onClick={handleCloseLearnMore} wide>
            {t("close.button")}
          </MobileFloatingButton>
        </>
      </MobilePopoverCard>

      <MobileInfoPopover
        isOpen={showWarningOnClose}
        title={t("unsavedChanges.title")}
        subtitle={t("unsavedChanges.subtitle")}
        onClose={() => shouldShowWarningOnClose(true)}
        primaryButtonText={t("unsavedChanges.goBack")}
        primaryButtonAction={() => shouldShowWarningOnClose(true)}
        secondaryButtonText={t("unsavedChanges.leaveAnyway")}
        secondaryButtonAction={() => shouldShowWarningOnClose(false)}
        className={styles.UnsavedChanges}
        closeIcon={<CloseIcon className={styles.Close} />}
      />
    </>
  );
};

export default PriceWatchPopoverContent;
